import { useState } from 'react';
import { useLocation,useNavigate } from 'react-router-dom';

import useStateHandler from '@/hooks/useStateHandler';
import GalleryService from '@/services/gallery.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import { getAboutErrors, getMediaErrors, getSubmitErrors } from '../../validations';

import './styles/SubmitArtworkDraft.css';

export default function SubmitArtworkDraft({ formState, setFormError, termsAccepted, auctionAgreementAccepted, onInvalidStep }) {
  const { cacheHandler, state, stateHandler } = useStateHandler();
  const username = state?.user_session?.user_username?.toLowerCase?.();
  const isAuctionAccount = username === 'tgallery';
  const userType = state?.user_session?.user_type;
  const isArtistAccount = userType === 'artist' || userType === 'gallery_artist';
  // Speedw drafts should validate against the auction starting price field.
  const priceFieldKey = isAuctionAccount ? 'price_starting_at' : 'artwork_price';
  const [loading, setLoading] = useState(false);
  const [openGenericPopup, setOpenGenericPopup] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isVerifyGalleryAddArtwork = location.pathname === '/verify-gallery/add-artwork';

  async function validations() {
    setFormError(null);

    const aboutErrors = getAboutErrors(formState, { priceFieldKey });
    const mediaErrors = getMediaErrors(formState);
    const rawErrors = getSubmitErrors(termsAccepted, auctionAgreementAccepted, isAuctionAccount, formState);
    const errors = isArtistAccount
      ? rawErrors.filter(
        (error) => error.element !== 'artwork_shipping_own' && error.element !== 'artwork_shipping_rest'
      )
      : rawErrors;

    if (aboutErrors.length > 0) {
      setFormError(aboutErrors);
      onInvalidStep(1);
      return false;
    }

    if (mediaErrors.length > 0) {
      setFormError(mediaErrors);
      onInvalidStep(2);
      return false;
    }

    if (errors.length > 0) {
      setFormError(errors);
      onInvalidStep(3);
      return false;
    }

    return true;
  }

  async function submitArtworkDraft(event) {
    event.preventDefault();
    if (!await validations()) return;
    
    const validationOptions = isArtistAccount
      ? { about: true, media: true }
      : { about: true, media: true, shipping: true };
    const payload = isArtistAccount
      ? { ...formState, draft_status: 'pending' }
      : formState;
    const { response, data } = await GalleryService.saveArtworkDraft(payload, validationOptions);

    if (response.ok) {
      if (isArtistAccount) {
        cacheHandler.triggerAction("NEW_ARTWORK_REQUEST");
        stateHandler.set("temporalPopup", {
          text: "Artwork sent for gallery approval",
          type: "navbar-present",
        });
        navigate('/profile', { replace: true });
        return;
      }
      setOpenGenericPopup(true);     
    } else if (response.status === 400) {
      setLoading(false);
      const serverElement = data?.error_data?.element || data?.element;
      const mappedElement = (isAuctionAccount && serverElement === 'artwork_price')
        ? 'price_starting_at'
        : serverElement;
      setFormError([{ element: mappedElement, error_code: data.error_type }]);
    }
  }

  function backToInventory() {
    setOpenGenericPopup(false);
    setLoading(false); 
    cacheHandler.triggerAction("NEW_ARTWORK_REQUEST");
    navigate('/inventory/pending', { replace: true });
  }

  function backToProfile() {
    setOpenGenericPopup(false);
    setLoading(false);
    cacheHandler.triggerAction("NEW_ARTWORK_REQUEST");
    navigate('/profile', { replace: true });
  }

  function closePopup() {
    setOpenGenericPopup(false);
    setLoading(false);
    cacheHandler.triggerAction("NEW_ARTWORK_REQUEST");
    if (isVerifyGalleryAddArtwork) {
      navigate('/gallery-onboarding', { replace: true });
    }
  }

  return (
    <>
      <div className="add-artwork-form-submit_button">
        <ContinueButton 
          onClick={submitArtworkDraft} 
          loading={loading} 
          disabled={loading}
        >
          {isArtistAccount ? 'Gallery Approval' : 'Submit artwork'}
        </ContinueButton>
      </div>

      <GenericPopup className="artwork-confirmation-dialog remove-scrollbar" open={openGenericPopup} opacity>
        <Text className="add-artwork-form__confirmation-text-header" paragraph >
          Submission received!
        </Text>

        {isVerifyGalleryAddArtwork ? (
          <>
            <Text className="add-artwork-form__confirmation-text" paragraph>
              Success. Your artwork is under review
            </Text>
            <div className="submit-artwork-confirmation-dialog__action-buttons">
              <ContinueButton
                onClick={closePopup}
                className="submit-artwork-confirmation-dialog__button continue-button_two"
              >
                Close
              </ContinueButton>
            </div>
          </>
        ) : (
          <>
            <Text className="add-artwork-form__confirmation-text" paragraph>
              Once accepted, the artwork will appear in your profile. You can check its submission status in your inventory at any time.
            </Text>
            <div className="submit-artwork-confirmation-dialog__action-buttons">
              <ContinueButton
                onClick={backToInventory}
                className="submit-artwork-confirmation-dialog__button continue-button_two"
              >
                Visit inventory
              </ContinueButton>
              <ContinueButton
                onClick={backToProfile}
                className="submit-artwork-confirmation-dialog__button continue-button_two"
              >
                Go to profile
              </ContinueButton>
            </div>
          </>
        )}
      </GenericPopup>
    </>
  );
}
